import React, { useMemo, useState } from "react";
import { Table } from "antd";
import { AlertTriangle } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import StatusBadge from "../../../components/common/StatusBadge";
import InfoTile from "../../../components/common/InfoTile";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetFuelReportSummaryQuery, useListFuelTransactionsQuery } from "../fuelApi";
import { FUEL_TXN_STATUS_CLASS, FUEL_TXN_STATUS_LABEL, formatDate, formatDateTime, litres, money, whole } from "../fuelFormat";
import FuelTransactionDetailModal from "../components/FuelTransactionDetailModal";
import VehicleTypeSelect from "../../masters/vehicles/components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../masters/vehicles/vehicleTypes";

const GROUPINGS = [
  { key: "vehicle", label: "Vehicle" },
  { key: "card", label: "Card" },
  { key: "user", label: "User" },
  { key: "month", label: "Month" },
  { key: "station", label: "Station" },
];

// Consumption figures only make sense where the vehicle actually moved between
// fills in the range - so both are blank unless distance > 0 (and litres > 0).
const kmPerLitre = (r) => (Number(r.distanceKm) > 0 && Number(r.totalLitres) > 0 ? Number(r.distanceKm) / Number(r.totalLitres) : null);
const costPerKm = (r) => (Number(r.distanceKm) > 0 ? Number(r.totalAmount || 0) / Number(r.distanceKm) : null);
const ratio = (n) => (n == null ? "-" : n.toFixed(2));

// Fuel Reports (Fuel.ViewReports). One summary table regrouped five ways over
// GET /fuel/reports/summary (voided purchases are excluded server-side), and
// under it the "Mismatched card usage" list the owner asked for by name: every
// purchase in the range made with a card that is not assigned to the vehicle.
const FuelReportsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [groupBy, setGroupBy] = useState("vehicle");
  // Vehicle type (Batch191) - applied server-side to the summary AND the
  // mismatched-card list, so every tile and total on the page is for that type.
  const [vehicleType, setVehicleType] = useState("");
  const [openGuid, setOpenGuid] = useState(null);
  const isVehicle = groupBy === "vehicle";
  const groupLabel = GROUPINGS.find((g) => g.key === groupBy).label;

  const range = {
    branchId: selectedBranchId ?? undefined,
    vehicleType: vehicleType || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  };
  const typeLine = `Vehicle type: ${vehicleType ? vehicleTypeLabel(vehicleType) : "All"}`;
  const { data: summaryData, isLoading, isFetching } = useGetFuelReportSummaryQuery({ groupBy, ...range });
  // The rows carry only a display label, and for the User grouping that is a
  // first name two people can share - so the table key is positional, never the label.
  const summary = useMemo(() => (summaryData || []).map((r, i) => ({ ...r, rowKey: `${groupBy}-${i}` })), [summaryData, groupBy]);
  const { data: mismatchPage, isLoading: isLoadingMismatch } = useListFuelTransactionsQuery({ ...range, mismatchOnly: true, pageSize: 2000 });
  const mismatchRows = mismatchPage?.items || [];

  const sum = (field) => summary.reduce((total, r) => total + Number(r[field] || 0), 0);
  const totals = {
    transactionCount: sum("transactionCount"),
    totalLitres: sum("totalLitres"),
    totalAmount: sum("totalAmount"),
    exceptionCount: sum("exceptionCount"),
    mismatchCount: sum("mismatchCount"),
    pendingReviewCount: sum("pendingReviewCount"),
    distanceKm: sum("distanceKm"),
  };
  // Fleet-wide consumption over only the vehicles that have a distance - adding
  // in the litres of a vehicle with no distance would understate km/L.
  const withDistance = summary.filter((r) => Number(r.distanceKm) > 0);
  const fleet = {
    distanceKm: totals.distanceKm,
    totalLitres: withDistance.reduce((t, r) => t + Number(r.totalLitres || 0), 0),
    totalAmount: withDistance.reduce((t, r) => t + Number(r.totalAmount || 0), 0),
  };

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const columns = [
    { title: groupLabel, dataIndex: "groupLabel", render: (v) => <span className="fw-semibold">{v || "(not given)"}</span> },
    { title: "Purchases", dataIndex: "transactionCount", align: "right" },
    { title: "Litres", dataIndex: "totalLitres", align: "right", render: (v) => litres(v) },
    { title: "Amount", dataIndex: "totalAmount", align: "right", render: (v) => money(v) },
    ...(isVehicle
      ? [
          { title: "Distance (km)", dataIndex: "distanceKm", align: "right", render: (v) => (Number(v) > 0 ? whole(v) : "-") },
          { title: "km / L", dataIndex: "kmPerLitre", align: "right", render: (_, r) => ratio(kmPerLitre(r)) },
          { title: "Cost / km", dataIndex: "costPerKm", align: "right", render: (_, r) => ratio(costPerKm(r)) },
        ]
      : []),
    { title: "Exceptions", dataIndex: "exceptionCount", align: "right", render: (v) => v || "-" },
    { title: "Mismatched card", dataIndex: "mismatchCount", align: "right", render: (v) => v || "-" },
    { title: "Pending review", dataIndex: "pendingReviewCount", align: "right", render: (v) => v || "-" },
    { title: "Last purchase", dataIndex: "lastTransactionDate", render: (v) => formatDate(v) },
  ];

  const exportColumns = [
    { header: groupLabel, value: (r) => r.groupLabel || "(not given)" },
    { header: "Purchases", value: (r) => r.transactionCount, align: "right" },
    { header: "Litres", value: (r) => litres(r.totalLitres), align: "right" },
    { header: "Amount", value: (r) => money(r.totalAmount), align: "right" },
    ...(isVehicle
      ? [
          { header: "Distance (km)", value: (r) => (Number(r.distanceKm) > 0 ? whole(r.distanceKm) : ""), align: "right" },
          { header: "km/L", value: (r) => (kmPerLitre(r) == null ? "" : ratio(kmPerLitre(r))), align: "right" },
          { header: "Cost/km", value: (r) => (costPerKm(r) == null ? "" : ratio(costPerKm(r))), align: "right" },
        ]
      : []),
    { header: "Exceptions", value: (r) => r.exceptionCount, align: "right" },
    { header: "Mismatched Card", value: (r) => r.mismatchCount, align: "right" },
    { header: "Pending Review", value: (r) => r.pendingReviewCount, align: "right" },
    { header: "Last Purchase", value: (r) => formatDate(r.lastTransactionDate) },
  ];
  const exportTotals = {
    [groupLabel]: "Total",
    Purchases: totals.transactionCount,
    Litres: litres(totals.totalLitres),
    Amount: money(totals.totalAmount),
    ...(isVehicle
      ? {
          "Distance (km)": whole(totals.distanceKm),
          "km/L": ratio(kmPerLitre(fleet)),
          "Cost/km": ratio(costPerKm(fleet)),
        }
      : {}),
    Exceptions: totals.exceptionCount,
    "Mismatched Card": totals.mismatchCount,
    "Pending Review": totals.pendingReviewCount,
  };

  const mismatchColumns = [
    {
      title: "Vehicle",
      dataIndex: "regNo",
      render: (v, r) => (
        <>
          <span className="fw-semibold">{v}</span>
          <div className="small text-muted">{r.transactionNumber}</div>
        </>
      ),
    },
    { title: "Card used", dataIndex: "cardNumber", render: (v, r) => <>{v}<div className="small text-muted">{r.provider}</div></> },
    {
      title: "Expected (vehicle's default card)",
      dataIndex: "vehicleDefaultCardNumber",
      render: (v) => v || <span className="text-muted">no default card set</span>,
    },
    { title: "User", dataIndex: "recordedByName", render: (v) => v || "-" },
    { title: "Date", dataIndex: "transactionDate", render: (v) => <span className="text-nowrap">{formatDateTime(v)}</span> },
    { title: "Litres", dataIndex: "litres", align: "right", render: (v) => litres(v) },
    { title: "Amount", dataIndex: "totalAmount", align: "right", render: (v) => money(v) },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} classMap={FUEL_TXN_STATUS_CLASS} labelMap={FUEL_TXN_STATUS_LABEL} /> },
  ];
  const mismatchExportColumns = [
    { header: "No", value: (r) => r.transactionNumber },
    { header: "Vehicle", value: (r) => r.regNo },
    { header: "Card Used", value: (r) => r.cardNumber },
    { header: "Expected Card", value: (r) => r.vehicleDefaultCardNumber || "none set" },
    { header: "User", value: (r) => r.recordedByName || "" },
    { header: "Date", value: (r) => formatDateTime(r.transactionDate) },
    { header: "Litres", value: (r) => litres(r.litres), align: "right" },
    { header: "Amount", value: (r) => money(r.totalAmount), align: "right" },
    { header: "Status", value: (r) => FUEL_TXN_STATUS_LABEL[r.status] || r.status },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Fuel Reports</h4>
              <h6>Fuel bought by vehicle, card, user, month or station - and every time a mismatched card was used</h6>
            </div>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md"><InfoTile label="Purchases" value={totals.transactionCount} /></div>
          <div className="col-6 col-md"><InfoTile label="Litres" value={litres(totals.totalLitres)} /></div>
          <div className="col-6 col-md"><InfoTile label="Amount" tone="maroon" value={money(totals.totalAmount)} /></div>
          <div className="col-6 col-md"><InfoTile label="Pending review" value={totals.pendingReviewCount} /></div>
          <div className="col-6 col-md"><InfoTile label="Mismatched card" value={totals.mismatchCount} /></div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-start">
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <VehicleTypeSelect className="form-control form-control-sm" style={{ width: 150, height: 34 }} value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
                  <span className="small text-muted">Group by</span>
                  <div className="btn-group btn-group-sm" role="group">
                    {GROUPINGS.map((g) => (
                      <button
                        key={g.key}
                        type="button"
                        className={`btn ${groupBy === g.key ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setGroupBy(g.key)}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>
                <ListExportToolbar
                  title={`Fuel Report by ${groupLabel}`}
                  subtitleLines={[`Branch: ${branchCode || "All"}`, typeLine, `Date: ${dateFrom} to ${dateTo}`, "Voided purchases excluded"]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={summary}
                  totals={exportTotals}
                  fileName={`FuelReportBy${groupLabel}`}
                  orientation="landscape"
                />
              </div>
            </div>

            <div className="table-responsive mt-2">
              <DataTable
                columns={columns}
                dataSource={summary}
                rowKey="rowKey"
                loading={isLoading || isFetching}
                summary={() =>
                  summary.length > 0 ? (
                    <Table.Summary.Row style={{ fontWeight: 700, background: "#F8F9FA" }}>
                      <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">{totals.transactionCount}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">{litres(totals.totalLitres)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">{money(totals.totalAmount)}</Table.Summary.Cell>
                      {isVehicle && (
                        <>
                          <Table.Summary.Cell index={4} align="right">{totals.distanceKm > 0 ? whole(totals.distanceKm) : "-"}</Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">{ratio(kmPerLitre(fleet))}</Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">{ratio(costPerKm(fleet))}</Table.Summary.Cell>
                        </>
                      )}
                      <Table.Summary.Cell index={isVehicle ? 7 : 4} align="right">{totals.exceptionCount}</Table.Summary.Cell>
                      <Table.Summary.Cell index={isVehicle ? 8 : 5} align="right">{totals.mismatchCount}</Table.Summary.Cell>
                      <Table.Summary.Cell index={isVehicle ? 9 : 6} align="right">{totals.pendingReviewCount}</Table.Summary.Cell>
                      <Table.Summary.Cell index={isVehicle ? 10 : 7} />
                    </Table.Summary.Row>
                  ) : null
                }
              />
            </div>
            <small className="text-muted d-block mt-1">
              Voided purchases are left out.
              {isVehicle && " Distance is the odometer movement between a vehicle's fills in the range; km/L and cost/km are shown only where there is a distance, and the Total row works them out over those vehicles alone."}
            </small>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
              <div>
                <h5 className="mb-1 d-inline-flex align-items-center gap-2">
                  <AlertTriangle size={18} style={{ color: "#9A5B00" }} /> Mismatched card usage
                  <span className="badge rounded-pill" style={{ background: "#FFF4E0", color: "#9A5B00", border: "1px solid #F0B95B" }}>{mismatchRows.length}</span>
                </h5>
                <div className="small text-muted">
                  Purchases in this range made with a card that is not assigned to the vehicle. Shown for review - click a row to see the entry and its photos.
                </div>
              </div>
              <ListExportToolbar
                title="Mismatched Fuel Card Usage"
                subtitleLines={[`Branch: ${branchCode || "All"}`, typeLine, `Date: ${dateFrom} to ${dateTo}`]}
                branchCode={branchCode}
                columns={mismatchExportColumns}
                rows={mismatchRows}
                fileName="FuelMismatchedCardUsage"
                orientation="landscape"
              />
            </div>
            <div className="table-responsive">
              <DataTable
                columns={mismatchColumns}
                dataSource={mismatchRows}
                rowKey="recordGuid"
                loading={isLoadingMismatch}
                onRow={(record) => ({ onClick: () => setOpenGuid(record.recordGuid), style: { cursor: "pointer" } })}
              />
            </div>
          </div>
        </div>
      </div>

      <FuelTransactionDetailModal recordGuid={openGuid} onClose={() => setOpenGuid(null)} />
    </div>
  );
};

export default FuelReportsPage;
